import { z } from "zod";
import { isoDateSchema, uuidSchema } from "./common";

export const medicationCategorySchema = z.enum(["PRESCRIPTION", "OTC", "SUPPLEMENT", "HERBAL"]);
export const medicationRouteSchema = z.enum([
  "ORAL",
  "TOPICAL",
  "INJECTABLE",
  "INHALED",
  "SUBLINGUAL",
  "RECTAL",
  "OPHTHALMIC",
  "OTIC",
  "NASAL",
  "OTHER",
]);
export const medicationFrequencySchema = z.enum([
  "ONCE_DAILY",
  "TWICE_DAILY",
  "THREE_TIMES_DAILY",
  "FOUR_TIMES_DAILY",
  "EVERY_OTHER_DAY",
  "WEEKLY",
  "AS_NEEDED",
  "OTHER",
]);

export const patientMedicationInputSchema = z
  .object({
    medicationId: uuidSchema.optional(),
    freeTextName: z.string().trim().min(1).max(200).optional(),
    freeTextNameAr: z.string().trim().max(200).optional(),
    category: medicationCategorySchema,
    dose: z.string().trim().max(100).optional(),
    route: medicationRouteSchema.optional(),
    frequency: medicationFrequencySchema.optional(),
    frequencyNotes: z.string().trim().max(300).optional(),
    prescribedBy: z.string().trim().max(150).optional(),
    indication: z.string().trim().max(300).optional(),
    startDate: isoDateSchema.optional(),
    endDate: isoDateSchema.optional(),
    notes: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.medicationId || v.freeTextName, {
    message: "Select a medication from the catalog or enter its name",
    path: ["freeTextName"],
  });

export type PatientMedicationInput = z.infer<typeof patientMedicationInputSchema>;

export const allergySeveritySchema = z.enum(["MILD", "MODERATE", "SEVERE", "LIFE_THREATENING", "UNKNOWN"]);
export const allergenTypeSchema = z.enum(["MEDICATION", "FOOD", "ENVIRONMENTAL", "OTHER"]);

export const patientAllergyInputSchema = z
  .object({
    allergyId: uuidSchema.optional(),
    freeTextAllergen: z.string().trim().min(1).max(200).optional(),
    freeTextAllergenAr: z.string().trim().max(200).optional(),
    allergenType: allergenTypeSchema.default("OTHER"),
    reaction: z.string().trim().max(300).optional(),
    severity: allergySeveritySchema,
    onsetDate: isoDateSchema.optional(),
    notes: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.allergyId || v.freeTextAllergen, {
    message: "Select an allergen from the catalog or enter it",
    path: ["freeTextAllergen"],
  });

export type PatientAllergyInput = z.infer<typeof patientAllergyInputSchema>;

export const conditionStatusSchema = z.enum(["ACTIVE", "RESOLVED", "CHRONIC_MANAGED", "IN_REMISSION"]);

export const patientConditionInputSchema = z
  .object({
    conditionId: uuidSchema.optional(),
    freeTextName: z.string().trim().min(1).max(200).optional(),
    freeTextNameAr: z.string().trim().max(200).optional(),
    diagnosedDate: isoDateSchema.optional(),
    status: conditionStatusSchema.default("ACTIVE"),
    notes: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.conditionId || v.freeTextName, {
    message: "Select a condition from the catalog or enter it",
    path: ["freeTextName"],
  });

export type PatientConditionInput = z.infer<typeof patientConditionInputSchema>;
